import { fetchGet } from "Services/api";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { isNotEmptyArray } from "Services/functions";
import Margin from "Components/Stats/Margin";
import { getOffersByCategoryByMonthData } from "Components/Stats/Data/Offres/Categories";
import { DateTime } from "luxon";
import { Calendar } from "primereact/calendar";
import NivoBar from "Components/Stats/Charts/NivoBar";

/**
 * Composant 'OffersByCategoryByMonth'.
 *
 * Ce composant affiche un graphique du nombre d'offres publiées par catégorie par mois dans une université,
 * sur une période donnée. Les données sont récupérées à partir de l'API et formatées pour le graphique
 * à l'aide de fonctions spécifiques. Les dates de début et de fin de la période choisie sont gérées via
 * l'état local du composant avec le hook useState.
 *
 *  - {@link getOffersByCategoryByMonthData} : Fonction qui formate les données pour le graphique du nombre d'offres publiées par catégorie par mois.
 *
 *  - {@link NivoBar} : Composant générique de graphique en barres implémenté avec la librairie Nivo.
 *
 *
 * @param {Object} props - Les propriétés du composant.
 * @param {Object} props.auth - Les informations d'authentification de l'utilisateur. (depuis Redux)
 * @param {Object} props.secondaryTables - Les données des tables secondaires. (depuis Redux)
 * @param {Object} props.states - Les états des offres. (depuis Redux)
 * @param {string} props.universityName - Le nom de l'université pour laquelle afficher les statistiques.
 * @returns {JSX.Element} Le graphique du nombre d'offres publiées par catégorie par mois.
 *
 *
 * @example
 * // Utilisation du composant 'OffersByCategoryByMonth' dans un autre composant.
 * import OffersByCategoryByMonth from './OffersByCategoryByMonth';
 * // ...
 * <OffersByCategoryByMonth
 *   universityName="Nom de l'université"
 * />
 */
const OffersByCategoryByMonth = (props) => {
  /* Données pour graphique des offres par catégorie par mois*/
  const [offersByCategoryByMonthPeriod, setOffersByCategoryByMonthPeriod] =
    useState([
      DateTime.local()
        .startOf("month")
        .minus({ months: 6 })
        .plus({ days: 1 })
        .toJSDate(),
      DateTime.local().endOf("month").minus({ months: 1 }).toJSDate(),
    ]);

  const [offersByCategoryByMonth, setOffersByCategoryByMonth] = useState([]);

  /**
   * Mise à jour des données pour les offres par catégorie par mois (publiées sur une période)
   */
  async function updateOffersCountCategoryByMonth() {
    let requestData = await fetchGet(
      "/offers/stats/count/category",
      props.auth.token,
      {
        // Filtre période, posté après la date de début et avant la date de fin
        "postedAt[after]": offersByCategoryByMonthPeriod[0]
          .toISOString()
          .slice(0, 10),
        "postedAt[before]": offersByCategoryByMonthPeriod[1]
          .toISOString()
          .slice(0, 10),
        // Filtre état: publié
        "state[]": [
          props.states.statesData.find(
            (state) => state.statusLabel === "Publié"
          ).id,
        ],
      }
    );

    setOffersByCategoryByMonth(
      getOffersByCategoryByMonthData(
        requestData,
        props.secondaryTables.offerCategoriesData,
        offersByCategoryByMonthPeriod[0],
        offersByCategoryByMonthPeriod[1]
      )
    );
  }

  useEffect(async () => {
    // Si les données API sont chargées et que les données de catégories sont chargées
    // Et que la période est définie, et correcte
    if (
      isNotEmptyArray(props.secondaryTables.offerCategoriesData) &&
      offersByCategoryByMonthPeriod[0] instanceof Date &&
      !isNaN(offersByCategoryByMonthPeriod[0]) &&
      offersByCategoryByMonthPeriod[1] instanceof Date &&
      !isNaN(offersByCategoryByMonthPeriod[1])
    )
      await updateOffersCountCategoryByMonth();
  }, [
    offersByCategoryByMonthPeriod,
    props.secondaryTables.offerCategoriesData,
  ]);

  return (
    <>
      {/* Graphique des offres par catégorie par mois (sur une période donnée) */}
      <div className="calendar">
        <label>Période choisie</label>
        <Calendar
          value={offersByCategoryByMonthPeriod}
          onChange={(e) => {
            if (e.value[0] && e.value[1]) {
              let beforeDate = DateTime.fromJSDate(e.value[0]);
              let afterDate = DateTime.fromJSDate(e.value[1]).plus({
                months: 1,
              });
              setOffersByCategoryByMonthPeriod([
                beforeDate.toJSDate(),
                afterDate.toJSDate(),
              ]);
            } else {
              setOffersByCategoryByMonthPeriod(e.value);
            }
          }}
          showIcon
          view="month"
          dateFormat="mm/yy"
          selectionMode="range"
          maxDate={DateTime.local().minus({ months: 1 }).toJSDate()}
        />
      </div>
      <Margin position="bottom" size="xs" />
      <NivoBar
        data={offersByCategoryByMonth}
        keysNames={props.secondaryTables.offerCategoriesData.map(
          (category) => category.label
        )}
        legendYAxis="Offres par catégorie"
        indexName="date"
        title={
          "Nombre d'offres publiées par catégorie par mois dans l'université " +
          props.universityName
        }
        displayPercentage
      />
      <Margin position="bottom" size="xs" />
    </>
  );
};

OffersByCategoryByMonth.propTypes = {
  auth: PropTypes.object.isRequired,
  secondaryTables: PropTypes.object.isRequired,
  states: PropTypes.object.isRequired,
  universityName: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  states: state.states,
  auth: state.auth,
  secondaryTables: state.secondaryTables,
});

export default connect(mapStateToProps)(OffersByCategoryByMonth);
